import { Conversation, ConversationContent, ConversationScrollButton } from '../../ai-elements/conversation.js';
import { Suggestions, Suggestion } from '../../ai-elements/suggestion.js';
import React__default, { memo, useMemo } from 'react';
import { useConversationScrollDetection } from '../hooks/useConversationScrollDetection.js';
import ConciergeWelcome from './ConciergeWelcome.js';
import { ConversationSkeleton } from './ConversationSkeleton.js';
import { MessageListV2 } from './MessageListV2.js';
import { PromptInputConcierge } from './PromptInputConcierge.js';

// Separate input component that can re-render without affecting conversation area
const ConciergeInputArea = ({ searchInputValue, setSearchInputValue, handleSearch, isActivelyStreaming, conversationHistoryLength, shouldShowSuggestions, showSuggestions, followUpQuestions, }) => {
    const placeholder = useMemo(() => (conversationHistoryLength > 0 ? 'Ask a follow up question' : 'How can I help you today?'), [conversationHistoryLength]);
    const suggestionsSection = useMemo(() => {
        if (!shouldShowSuggestions)
            return null;
        return (React__default.createElement("div", { className: `tw:mx-auto tw:w-full tw:max-w-[768px] tw:transition-all tw:duration-300 tw:ease-out ${showSuggestions
                ? 'tw:opacity-100 tw:translate-y-0 tw:max-h-96 tw:pointer-events-auto'
                : 'tw:opacity-0 tw:translate-y-2 tw:max-h-0 tw:overflow-hidden tw:pointer-events-none'}`, style: {
                transitionDelay: showSuggestions ? '100ms' : '0ms',
            } },
            React__default.createElement(Suggestions, null, followUpQuestions.map((question) => (React__default.createElement(Suggestion, { key: question, suggestion: question, onClick: (suggestion) => setSearchInputValue(suggestion) }))))));
    }, [shouldShowSuggestions, showSuggestions, followUpQuestions, setSearchInputValue]);
    return (React__default.createElement("div", { className: "tw:flex tw:shrink-0 tw:flex-col tw:gap-4 tw:px-4 tw:pt-4 md:tw:px-0", style: { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' } },
        suggestionsSection,
        React__default.createElement(PromptInputConcierge, { placeholder: placeholder, value: searchInputValue, onChange: setSearchInputValue, onSubmit: handleSearch, isStreaming: isActivelyStreaming })));
};
const ConciergeChatAreaV2Component = ({ appName, brandId, conversationId, isEmbedded = false, apiBaseUrl, conversationHistory, productMetadataMap, streamingProductGroups, previewSearchQueries, previewQueryProducts, followUpQuestions, streamingFeaturedProduct, isActivelyStreaming, streamingAIResponse, currentStatus, isWaitingForFollowUpQueries, isLoadingConversationQuery, handleSearch, onRetry, onUpdateFeedback, searchInputValue, setSearchInputValue, }) => {
    // Derived state
    const showWelcome = conversationHistory.length === 0 && !isLoadingConversationQuery;
    // Show typing indicator only when actively streaming but no search queries/products AND no text content yet
    const showTypingIndicator = isActivelyStreaming &&
        !streamingAIResponse &&
        previewSearchQueries.length === 0 &&
        previewQueryProducts.length === 0;
    // Scroll-based suggestions animation
    // Memoize the hook call to prevent re-renders when searchInputValue changes
    const scrollDetectionParams = useMemo(() => ({
        followUpQuestions,
        isActivelyStreaming,
        conversationHistoryLength: conversationHistory.length,
        showWelcome,
        searchInputValue: '', // Not actually used in the hook, but required by interface
    }), [followUpQuestions, isActivelyStreaming, conversationHistory.length, showWelcome]);
    const { showSuggestions, shouldShowSuggestions, bottomRef } = useConversationScrollDetection(scrollDetectionParams);
    // Memoize handleSearch callback to prevent welcome component re-renders
    const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);
    // Memoize welcome component props to prevent re-renders
    const welcomeProps = useMemo(() => ({
        onSearch: stableHandleSearch,
        brandId: brandId ? parseInt(brandId) : undefined,
        isEmbedded,
        apiBaseUrl,
    }), [stableHandleSearch, brandId, isEmbedded, apiBaseUrl]);
    // Memoize the conversation content to prevent re-renders when searchInputValue changes
    const conversationContent = useMemo(() => {
        if (isLoadingConversationQuery) {
            return React__default.createElement(ConversationSkeleton, null);
        }
        if (showWelcome) {
            return React__default.createElement(ConciergeWelcome, Object.assign({}, welcomeProps));
        }
        return (React__default.createElement(MessageListV2, { conversationId: conversationId, messages: conversationHistory, showTypingIndicator: showTypingIndicator, streamingAIResponse: streamingAIResponse, productMetadataMap: productMetadataMap, streamingProductGroups: streamingProductGroups, currentStatus: currentStatus, previewSearchQueries: previewSearchQueries, previewQueryProducts: previewQueryProducts, isWaitingForFollowUpQueries: isWaitingForFollowUpQueries, isActivelyStreaming: isActivelyStreaming, streamingFeaturedProduct: streamingFeaturedProduct, onSearch: handleSearch, onRetry: onRetry, onUpdateFeedback: onUpdateFeedback, bottomRef: bottomRef }));
    }, [
        isLoadingConversationQuery,
        showWelcome,
        welcomeProps,
        conversationId,
        conversationHistory,
        showTypingIndicator,
        streamingAIResponse,
        productMetadataMap,
        streamingProductGroups,
        currentStatus,
        previewSearchQueries,
        previewQueryProducts,
        isWaitingForFollowUpQueries,
        isActivelyStreaming,
        streamingFeaturedProduct,
        handleSearch,
        onRetry,
        onUpdateFeedback,
        bottomRef,
    ]);
    return (React__default.createElement("div", { className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: {
            minHeight: 0,
            maxHeight: '100%',
            overflow: 'hidden',
        } },
        React__default.createElement("div", { style: {
                flex: 1,
                overflow: showWelcome ? 'auto' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
            } }, showWelcome ? (React__default.createElement("div", { style: {
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            } }, conversationContent)) : (React__default.createElement(Conversation, null,
            React__default.createElement(ConversationContent, { className: "tw:flex tw:items-center tw:justify-center" }, conversationContent),
            React__default.createElement(ConversationScrollButton, null)))),
        React__default.createElement(ConciergeInputArea, { searchInputValue: searchInputValue, setSearchInputValue: setSearchInputValue, handleSearch: handleSearch, isActivelyStreaming: isActivelyStreaming, conversationHistoryLength: conversationHistory.length, shouldShowSuggestions: shouldShowSuggestions, showSuggestions: showSuggestions, followUpQuestions: followUpQuestions })));
};
// Memoize the chat area - input is separated so it can re-render without affecting conversation
const ConciergeChatAreaV2 = memo(ConciergeChatAreaV2Component);

export { ConciergeChatAreaV2 };
//# sourceMappingURL=ConciergeChatAreaV2.js.map
